'use client'

import { Box, Spinner } from '@chakra-ui/react'
import type { AxiosResponse } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useApiContext } from '@/utils/api'
import { loadPdfJs } from '@/utils/pdfJs'

const isProbablyPdf = (arrayBuffer: ArrayBuffer) => {
  if (arrayBuffer.byteLength < 5) {
    return false
  }

  const signature = new Uint8Array(arrayBuffer.slice(0, 5))
  return (
    signature[0] === 0x25 &&
    signature[1] === 0x50 &&
    signature[2] === 0x44 &&
    signature[3] === 0x46 &&
    signature[4] === 0x2d
  )
}

function PdfTileFallback({
  companySlug,
  section,
  attachmentId,
  isPreview,
  organisationId,
  sourceUrl,
  onOrientationDetected,
}: {
  companySlug: string
  section: string
  attachmentId: string
  isPreview: boolean
  organisationId: string
  sourceUrl?: string | null
  onOrientationDetected?: (isPortrait: boolean) => void
}) {
  const { api, anonymousApi } = useApiContext()
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [pdfjsLoaded, setPdfjsLoaded] = useState(false)
  const [pdfjsFailed, setPdfjsFailed] = useState(false)
  const abortedRef = useRef<boolean>(false)

  // Load PDF.js dynamically on client side only
  useEffect(() => {
    const loadPdfjs = async () => {
      try {
        await loadPdfJs()
        setPdfjsLoaded(true)
      } catch (error) {
        console.error('Failed to load PDF.js:', error)
        setPdfjsFailed(true)
        setIsLoading(false)
      }
    }

    // Only load on client side
    if (typeof window !== 'undefined') {
      loadPdfjs()
    }
  }, [])

  useEffect(() => {
    if (!pdfjsLoaded || typeof window === 'undefined') return

    abortedRef.current = false
    const render = async () => {
      try {
        setIsLoading(true)
        setThumbnailUrl(null)

        let arrayBuffer: ArrayBuffer

        if (sourceUrl) {
          const response = await fetch(sourceUrl)
          if (!response.ok) {
            throw new Error('Failed to load published PDF preview')
          }
          arrayBuffer = await response.arrayBuffer()
        } else if (isPreview) {
          const res: AxiosResponse<Blob> = await api.get(
            `/admin/investor-relations/${organisationId}/documents/${section}/${attachmentId}`,
            {
              responseType: 'blob',
            }
          )
          arrayBuffer = await res.data.arrayBuffer()
        } else {
          const res: AxiosResponse<Blob> = await anonymousApi.get(
            `/investor-relations/${companySlug}/documents/${section}/${attachmentId}`,
            {
              responseType: 'blob',
            }
          )
          arrayBuffer = await res.data.arrayBuffer()
        }
        if (!isProbablyPdf(arrayBuffer)) {
          setThumbnailUrl(null)
          return
        }

        const pdfjsLib = await loadPdfJs()

        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
        const pdf = await loadingTask.promise
        if (abortedRef.current) return

        const page = await pdf.getPage(1)
        const unscaledViewport = page.getViewport({ scale: 1 })
        onOrientationDetected?.(
          unscaledViewport.height > unscaledViewport.width
        )

        // Use device pixel ratio for crisp rendering on high-DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1
        const scale = Math.min(devicePixelRatio * 1.2, 2.0) // Cap at 2x for performance

        const viewport = page.getViewport({ scale })

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) throw new Error('Could not get canvas context')

        // Set canvas size to high resolution
        canvas.width = viewport.width
        canvas.height = viewport.height

        // Improve rendering quality
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = 'high'

        const renderTask = page.render({
          canvasContext: context,
          viewport,
          canvas,
        })
        await renderTask.promise
        if (abortedRef.current) return

        const dataUrl = canvas.toDataURL('image/png')
        setThumbnailUrl(dataUrl)
      } catch (e) {
        setThumbnailUrl(null)
      } finally {
        if (!abortedRef.current) setIsLoading(false)
      }
    }

    render()
    return () => {
      abortedRef.current = true
    }
  }, [
    api,
    anonymousApi,
    attachmentId,
    pdfjsLoaded,
    organisationId,
    isPreview,
    section,
    companySlug,
    sourceUrl,
    onOrientationDetected,
  ])

  return (
    <Box
      w="100%"
      h="100%"
      bg="gray.100"
      position="relative"
      overflow="hidden"
      style={{ aspectRatio: '16 / 9' }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {pdfjsFailed ? (
        <Box w="100%" h="100%" bg="gray.200" />
      ) : !pdfjsLoaded ? (
        <Spinner />
      ) : isLoading ? (
        <Spinner />
      ) : thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="PDF preview"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Box w="100%" h="100%" bg="gray.200" />
      )}
    </Box>
  )
}

export default PdfTileFallback
